import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment';

import { PageHeader, Dropdown, Menu, Button, Modal, Tabs, Tag, Badge, Table, Radio } from 'antd';
import {
  EditOutlined,
  EllipsisOutlined,
  CloseOutlined,
  FunnelPlotFilled,
  SwapOutlined,
  ShoppingOutlined,
  DeleteFilled,
} from '@ant-design/icons';
import ColorHash from 'color-hash';

import * as ToolRequest from '../../tools/ToolRequest';

import SpinFull from '../../components/SpinFull';
import SuperTable from '../../components/SuperTable';
import SuperTableColumn from '../../components/SuperTableColumn';
import CropNameBadge from '../../components/CropNameBadge';
import FarmProductionRecordDaySummary from './FarmProductionRecordDaySummary';

const colorHash = new ColorHash();

const typeLabels = {
  SOW: 'Sowing',
  TRANSPLANT: 'Transplanting',
  HARVEST: 'Harvesting',
  WASTE: 'Wastage',
};

const typeTagColors = {
  SOW: '#42A5F5',
  TRANSPLANT: '#4CAF50',
  HARVEST: '#FF9800',
};

const typeIcons = {
  SOW: FunnelPlotFilled,
  TRANSPLANT: SwapOutlined,
  HARVEST: ShoppingOutlined,
  WASTE: DeleteFilled,
};

const typeOrder = ['SOW', 'TRANSPLANT', 'HARVEST', 'WASTE'];

const sourceLabels = {
  LOCAL: 'Local',
  EXTERNAL: 'External',
};

const sourceTagColors = {
  LOCAL: 'blue',
  EXTERNAL: 'purple',
};

const stageLabels = {
  PROPAGATION: 'Propagation',
  PRODUCTION: 'Production',
};

const sourceOptions = [
  { value: 'LOCAL', label: 'Local' },
  { value: 'EXTERNAL', label: 'External' },
];

const stageOptions = [
  { value: 'PROPAGATION', label: 'Propagation' },
  { value: 'PRODUCTION', label: 'Production' },
];

const formatNumber = (value) => {
  if (value == null || value === '') {
    return '-';
  }
  return Number(value).toLocaleString();
};

const formatGroupDate = (date) => {
  const today = moment().format('YYYY-MM-DD');
  const yesterday = moment().subtract(1, 'day').format('YYYY-MM-DD');
  if (date === today) {
    return `${date} (Today)`;
  }
  if (date === yesterday) {
    return `${date} (Yesterday)`;
  }
  return date;
};

export default function FarmProductionRecordViewScreen(props) {
  const history = useHistory();

  const [loaded, setLoaded] = useState(false);
  const [tableKey, setTableKey] = useState(Math.random().toString());
  const [searchSiteOptions, setSearchSiteOptions] = useState([]);
  const [searchCropOptions, setSearchCropOptions] = useState([]);
  const [cropsCompanyId, setCropsCompanyId] = useState(null);
  const [activeType, setActiveType] = useState('all');
  const [viewMode, setViewMode] = useState('summary');
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const recordColumns = [
    {
      template: 'action',
      key: 'action',
      width: 80,
      render: (value, record) => {
        return (
          <Dropdown
            overlay={
              <Menu onClick={value => {
                switch (value.key) {
                  case 'edit': {
                    history.push(`/farm-production-record/edit/${record.id}`);
                    break;
                  }
                  case 'remove': {
                    Modal.confirm({
                      title: 'Confirm to REMOVE production record',
                      content: 'Are you sure to remove this production record?',
                      onOk: () => {
                        (async () => {
                          await ToolRequest.request('DELETE', `/v1/farm-production-record/${record.id}`);

                          setTableKey(Math.random().toString());
                        })();
                      },
                    });
                    break;
                  }
                }
              }}>
                <Menu.Item key="edit">
                  <EditOutlined />
                  Edit
                </Menu.Item>
                <Menu.Item key="remove">
                  <CloseOutlined />
                  Remove
                </Menu.Item>
              </Menu>
            }
          >
            <Button
              type="default"
              shape="round"
            >
              <EllipsisOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
    {
      template: 'id',
      key: 'id',
      width: 40,
    },
    {
      key: 'type',
      title: 'Type',
      width: 140,
      render: (value) => {
        const label = typeLabels[value] || value;
        if (!typeLabels[value]) {
          return label;
        }
        return <Tag color={typeTagColors[value]}>{label}</Tag>;
      },
    },
    {
      key: 'cropName',
      title: 'Crop',
      width: 200,
      render: (value) => <CropNameBadge name={value} />,
    },
    {
      key: 'amount',
      title: 'Amount',
      width: 100,
      render: (value) => formatNumber(value),
    },
    {
      key: 'source',
      title: 'Source',
      width: 110,
      render: (value, record) => {
        if (record.type === 'HARVEST' || record.type === 'WASTE') {
          return '-';
        }
        const source = value || 'LOCAL';
        return (
          <Tag color={sourceTagColors[source] || sourceTagColors.LOCAL}>
            {sourceLabels[source] || sourceLabels.LOCAL}
          </Tag>
        );
      },
    },
    {
      key: 'stage',
      title: 'Stage',
      width: 110,
      render: (value, record) => {
        if (record.type !== 'WASTE') {
          return '-';
        }
        return stageLabels[value] || '-';
      },
    },
    {
      key: 'weight',
      title: 'Weight (g)',
      width: 110,
      render: (value) => formatNumber(value),
    },
    {
      key: 'zone',
      title: 'Zone',
      width: 120,
      render: (value) => {
        if (!value) {
          return '-';
        }
        return <Badge count={value} style={{ backgroundColor: colorHash.hex(value) }} />;
      },
    },
    {
      key: 'siteName',
      title: 'Site',
    },
    {
      template: 'datetime',
      key: 'recordedAt',
      title: 'Recorded At',
    },
    {
      key: 'remarks',
      title: 'Remarks',
    },
  ];

  const columns = [
    {
      key: 'date',
      title: 'Date',
      width: 220,
      render: (value) => formatGroupDate(value),
    },
    {
      key: 'counts',
      title: 'Records',
      render: (value) => {
        const counts = value || {};
        const items = typeOrder
          .filter(type => counts[type])
          .map(type => (
            <Tag key={type} color={typeTagColors[type]}>
              {counts[type]} {typeLabels[type]}
            </Tag>
          ));
        return items.length ? items : '-';
      },
    },
  ];

  const searchColumns = [
    {
      template: 'select',
      key: 'siteId',
      title: 'Site',
      options: searchSiteOptions,
      defaultValue: searchSiteOptions[0]?.value,
      allowClear: false,
    },
    {
      template: 'select',
      key: 'cropId',
      title: 'Crop',
      options: searchCropOptions,
    },
    {
      template: 'select',
      key: 'source',
      title: 'Source',
      options: sourceOptions,
    },
    {
      template: 'select',
      key: 'stage',
      title: 'Stage',
      options: stageOptions,
    },
    {
      template: 'datePicker',
      key: 'beginAt',
      title: 'Recorded From',
    },
    {
      template: 'datePicker',
      key: 'endAt',
      title: 'Recorded Until',
    },
  ];

  const searchQueryParams = values => {
    const site = searchSiteOptions.find(s => s.value === values.siteId);
    const cropId = (
      values.cropId
      && site?.companyId === cropsCompanyId
      && searchCropOptions.some(o => o.value === values.cropId)
    ) ? values.cropId : null;
    return {
      siteId: values.siteId,
      cropId,
      source: values.source || null,
      stage: values.stage || null,
      beginAt: values.beginAt ? values.beginAt.toISOString() : null,
      endAt: values.endAt ? values.endAt.toISOString() : null,
    };
  };

  const loadCrops = async (companyId) => {
    if (!companyId) {
      setSearchCropOptions([]);
      setCropsCompanyId(null);
      return;
    }
    const cropsRes = await ToolRequest.request('GET', '/v1/farm-crop', {
      companyId,
      count: 1000,
    });
    setSearchCropOptions(
      cropsRes.data
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(crop => ({
          value: crop.id,
          label: <CropNameBadge name={crop.name} />,
        }))
    );
    setCropsCompanyId(companyId);
  };

  useEffect(() => {
    (async () => {
      const siteRes = await ToolRequest.request('GET', '/v1/farm-site', {
        count: 1000,
      });

      const options = siteRes.data.map(site => ({
        value: site.id,
        label: site.name,
        companyId: site.companyId,
      }));
      setSearchSiteOptions(options);

      if (options[0]) {
        await loadCrops(options[0].companyId);
      }

      setLoaded(true);
    })();
  }, []);

  const onSearchChange = (values) => {
    const site = searchSiteOptions.find(s => s.value === values.siteId);
    if (site?.companyId !== cropsCompanyId) {
      loadCrops(site?.companyId);
    }
  };

  const loadDataSource = async (body) => {
    const groupRes = await ToolRequest.request('GET', '/v1/farm-production-record-group', {
      ...body,
      type: activeType === 'all' ? undefined : activeType,
    });

    const site = body.siteId
      ? await ToolRequest.request('GET', `/v1/farm-site/${body.siteId}`).catch(() => null)
      : null;

    const data = groupRes.data.map(group => ({
      id: group.date,
      date: group.date,
      counts: group.counts,
      records: group.records.map(record => ({
        ...record,
        siteName: site?.name || '-',
        cropName: record.crop?.name || '-',
      })),
    }));

    setExpandedRowKeys(data.map(group => group.id));

    return {
      data,
      totalCount: groupRes.totalCount,
    };
  };

  const onTabChange = (key) => {
    setActiveType(key);
    setTableKey(Math.random().toString());
  };

  return loaded ? (
    <PageHeader
      title="Production Records"
      extra={[
        <Button
          key="sow"
          icon={<FunnelPlotFilled />}
          style={{ color: '#42A5F5', borderColor: '#42A5F5' }}
          onClick={() => history.push('/farm-production-record/add/sow')}
        >
          Add Sowing
        </Button>,
        <Button
          key="transplant"
          icon={<SwapOutlined />}
          style={{ color: '#4CAF50', borderColor: '#4CAF50' }}
          onClick={() => history.push('/farm-production-record/add/transplant')}
        >
          Add Transplanting
        </Button>,
        <Button
          key="harvest"
          icon={<ShoppingOutlined />}
          style={{ color: '#FF9800', borderColor: '#FF9800' }}
          onClick={() => history.push('/farm-production-record/add/harvest')}
        >
          Add Harvesting
        </Button>,
        <Button
          key="waste"
          icon={<DeleteFilled />}
          onClick={() => history.push('/farm-production-record/add/waste')}
        >
          Add Wastage
        </Button>,
      ]}
    >
      <Tabs
        activeKey={activeType}
        onChange={onTabChange}
        style={{ marginBottom: 16 }}
        tabBarExtraContent={
          <Radio.Group
            value={viewMode}
            onChange={e => setViewMode(e.target.value)}
            optionType="button"
            buttonStyle="solid"
            size="small"
          >
            <Radio.Button value="summary">Summary</Radio.Button>
            <Radio.Button value="list">List</Radio.Button>
          </Radio.Group>
        }
      >
        <Tabs.TabPane tab="All" key="all" />
        {['SOW', 'TRANSPLANT', 'HARVEST', 'WASTE'].map((type) => {
          const Icon = typeIcons[type];
          return (
            <Tabs.TabPane
              key={type}
              tab={(
                <span>
                  <Icon />
                  {typeLabels[type]}
                </span>
              )}
            />
          );
        })}
      </Tabs>
      <SuperTable
        searchColumns={searchColumns}
        searchQueryParams={searchQueryParams}
        tableKey={tableKey}
        onTableKeyChange={key => setTableKey(key)}
        onSearchChange={onSearchChange}
        loadDataSource={loadDataSource}
        columns={columns}
        pagination={{
          showTotal: (total, range) => `${range[0]} - ${range[1]} (Total ${total} dates)`,
        }}
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: setExpandedRowKeys,
          expandedRowRender: (group) => (
            viewMode === 'list' ? (
              <Table
                size="small"
                pagination={false}
                columns={recordColumns.map(column => SuperTableColumn(column))}
                dataSource={group.records.map(record => ({
                  ...record,
                  key: record.id,
                }))}
                scroll={{ x: true }}
              />
            ) : (
              <FarmProductionRecordDaySummary records={group.records} />
            )
          ),
        }}
      />
    </PageHeader>
  ) : <SpinFull />;
};
